import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { auditApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
} from '../../components/common';

/**
 * Audit log — FR-ADMIN-03.05
 * Immutable activity log, 7-year retention.
 */
export function AuditLogPage() {
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['audit-log', entityType, action],
    queryFn: () => auditApi.listLog({
      entityType: entityType || undefined,
      action: action || undefined,
      limit: 200,
    }),
  });

  const ENTITY_OPTIONS = [
    { value: '',              label: 'All entities' },
    { value: 'user',          label: 'User' },
    { value: 'product',       label: 'Product' },
    { value: 'pos_sale',      label: 'POS Sale' },
    { value: 'pos_shift',     label: 'POS Shift' },
    { value: 'customer',      label: 'Customer' },
    { value: 'supplier',      label: 'Supplier' },
    { value: 'journal_entry', label: 'Journal Entry' },
    { value: 'branch',        label: 'Branch' },
    { value: 'employee',      label: 'Employee' },
  ];

  const ACTION_OPTIONS = [
    { value: '',        label: 'All actions' },
    { value: 'create',  label: 'Create' },
    { value: 'update',  label: 'Update' },
    { value: 'delete',  label: 'Delete' },
    { value: 'login',   label: 'Login' },
    { value: 'logout',  label: 'Logout' },
    { value: 'void',    label: 'Void' },
    { value: 'post',    label: 'Post' },
    { value: 'approve', label: 'Approve' },
  ];

  const kpis = useMemo(() => {
    const rows = data ?? [];
    const uniqueUsers = new Set<string>();
    let last24h = 0;
    const now = Date.now();
    for (const r of rows) {
      if (r.user_email) uniqueUsers.add(r.user_email);
      if (now - new Date(r.created_at).getTime() < 24 * 3600 * 1000) last24h++;
    }
    return { total: rows.length, unique: uniqueUsers.size, last24h };
  }, [data]);

  return (
    <div className="px-8 py-10 max-w-7xl mx-auto">
      <PageHeader
        eyebrow="Administration"
        title="Audit log"
        subtitle="Immutable record of every significant action. Retained for 7 years per compliance."
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <KpiTile label="Events shown" value={kpis.total} />
        <KpiTile label="In last 24h" value={kpis.last24h} accent={kpis.last24h > 0 ? 'sage' : 'default'} />
        <KpiTile label="Unique actors" value={kpis.unique} />
      </div>

      {/* Filter bar */}
      <div className="card p-1.5 mb-4 flex gap-2 items-center flex-wrap">
        <div className="flex gap-1 bg-canvas-subtle rounded-pill p-1 overflow-x-auto max-w-full">
          {ENTITY_OPTIONS.map((e) => (
            <button
              key={e.value}
              onClick={() => setEntityType(e.value)}
              className={clsx(
                'px-3 py-1 text-xs font-semibold rounded-pill transition-all whitespace-nowrap',
                entityType === e.value
                  ? 'bg-canvas-elevated text-ink-900 shadow-xs'
                  : 'text-ink-600 hover:text-ink-900'
              )}
            >
              {e.label}
            </button>
          ))}
        </div>
      </div>

      <div className="card p-1.5 mb-4 flex gap-2 items-center flex-wrap">
        <div className="flex gap-1 bg-canvas-subtle rounded-pill p-1 overflow-x-auto max-w-full">
          {ACTION_OPTIONS.map((a) => (
            <button
              key={a.value}
              onClick={() => setAction(a.value)}
              className={clsx(
                'px-3 py-1 text-xs font-semibold rounded-pill transition-all whitespace-nowrap',
                action === a.value
                  ? 'bg-canvas-elevated text-ink-900 shadow-xs'
                  : 'text-ink-600 hover:text-ink-900'
              )}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="card"><LoadingState rows={6} /></div>
      ) : error ? (
        <ErrorState error={error} />
      ) : !data?.length ? (
        <div className="card">
          <EmptyState
            message="No audit events match"
            hint="Try clearing filters or pick a wider entity/action range."
          />
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Entity</th>
                  <th>Entity ID</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {data.map((e: any) => (
                  <tr key={e.id}>
                    <td className="text-xs text-ink-600 font-mono tabular-nums">
                      <div>{new Date(e.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</div>
                      <div className="text-ink-400">{new Date(e.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</div>
                    </td>
                    <td className="text-sm">
                      {e.user_email ? (
                        <>
                          <div className="font-semibold text-ink-900">{e.user_name}</div>
                          <div className="text-xs text-ink-500 truncate max-w-xs">{e.user_email}</div>
                        </>
                      ) : <span className="text-ink-400">System</span>}
                    </td>
                    <td>
                      <ActionBadge action={e.action} />
                    </td>
                    <td className="text-sm text-ink-700 font-mono">
                      {e.entity_type}
                    </td>
                    <td className="font-mono text-xs text-ink-500 tabular-nums">
                      {e.entity_id?.slice(0, 8) ?? '—'}
                    </td>
                    <td className="font-mono text-xs text-ink-500">
                      {e.ip_address ?? <span className="text-ink-300">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ActionBadge({ action }: { action: string }) {
  const tone =
    action === 'create'            ? 'bg-sage-50 text-sage-700 ring-sage-200' :
    action === 'update'            ? 'bg-blue-50 text-blue-700 ring-blue-200' :
    action === 'delete'            ? 'bg-red-50 text-red-700 ring-red-200' :
    action === 'void'              ? 'bg-red-50 text-red-700 ring-red-200' :
    action === 'login'             ? 'bg-canvas-subtle text-ink-700 ring-ink-200' :
    action === 'logout'            ? 'bg-canvas-subtle text-ink-500 ring-ink-200' :
    action === 'approve'           ? 'bg-sage-50 text-sage-700 ring-sage-200' :
    action === 'post'              ? 'bg-sage-50 text-sage-700 ring-sage-200' :
                                     'bg-canvas-subtle text-ink-700 ring-ink-200';
  return (
    <span className={clsx('badge ring-1 ring-inset capitalize', tone)}>
      {action}
    </span>
  );
}

function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}
